import axiosInstance from "./AxiosInstance";
import { clearCache } from "./requestCache";
import { resolveCourseId } from "../utils/courseId";

/** syllabusTopicCount and syllabusTopicPreview are served by the courses
 *  endpoint, which caches for 60s -- so a topic written here has to drop
 *  that cache or the list keeps showing the count from before the write. */
const invalidateCourseList = () => clearCache("courses:");

const API_URL = "/syllabustopics";

function normalizeListParams(params = {}) {
  const p = { ...params };
  if (p.courseId != null && p.courseId !== "") {
    const cid = resolveCourseId(p.courseId);
    if (cid) p.courseId = cid;
    else delete p.courseId;
  }
  return p;
}

export const listSyllabusTopics = async (params = {}) => {
  try {
    // Make sure limit is properly included in params
    const requestParams = normalizeListParams(params);
    
    // Ensure limit is passed as a number
    if (requestParams.limit) {
      requestParams.limit = Number(requestParams.limit);
    }
    
    console.log('Calling listSyllabusTopics with params:', requestParams);
    
    const response = await axiosInstance.get(`${API_URL}`, { 
      params: requestParams 
    });
    
    console.log('listSyllabusTopics response:', response.data);
    
    // Handle different possible response structures
    let topicsData = [];
    if (response.data && response.data.data) {
      topicsData = Array.isArray(response.data.data) ? response.data.data : [];
    } else if (response.data && Array.isArray(response.data)) {
      topicsData = response.data;
    } else if (response.data && response.data.topics) {
      topicsData = Array.isArray(response.data.topics) ? response.data.topics : [];
    }
    
    return {
      data: topicsData,
      pagination: response.data?.pagination || {},
      total: response.data?.total || topicsData.length
    };
  } catch (error) {
    console.error("Error in listSyllabusTopics:", error);
    throw error;
  }
};

/** Server caps syllabustopics list at 100 rows per page (see lms-api syllabustopic.service). */
export const SYLLABUS_TOPIC_PAGE_CAP = 100;
export const SYLLABUS_TOPIC_DEFAULT_LIMIT = 10;

// 🔹 Get syllabustopics page (use fetchAllSyllabusTopics to load every page)
export const getSyllabusTopics = async (limit = 10, page = 1, search = '', extraParams = {}) => {
  try {
    const safeLimit = Math.max(
      1,
      Math.min(SYLLABUS_TOPIC_PAGE_CAP, Number.parseInt(limit, 10) || SYLLABUS_TOPIC_DEFAULT_LIMIT)
    );
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const includeTotal = extraParams?.includeTotal !== false;
    const merged = normalizeListParams({
      limit: safeLimit,
      page: safePage,
      search: search || "",
      includeTotal: includeTotal ? "true" : "false",
      ...extraParams,
    });
    const response = await axiosInstance.get(API_URL, {
      params: merged,
    });
    const raw = response.data?.data ?? response.data;
    const data = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    return {
      data,
      pagination: response.data.pagination,
      total: response.data.total,
    };
  } catch (error) {
    console.error("Error fetching syllabus topics", error);
    throw error;
  }
};

/** Walk every page until empty or partial page — fixes list UI when totalPages is wrong or topics span many pages. */
export const fetchAllSyllabusTopics = async (search = '') => {
  const all = [];
  let page = 1;
  const maxPages = 500;
  while (page <= maxPages) {
    const res = await getSyllabusTopics(SYLLABUS_TOPIC_DEFAULT_LIMIT, page, search, { includeTotal: false });
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    if (chunk.length < SYLLABUS_TOPIC_DEFAULT_LIMIT) break;
    page += 1;
  }
  return all;
};

/** All topics for one course (paged on server) — use for popups / delete instead of loading every topic in the system. */
export const fetchTopicsForCourse = async (courseId, search = '') => {
  if (courseId == null || courseId === '') return [];
  const cid = resolveCourseId(courseId);
  if (!cid) return [];
  const all = [];
  let page = 1;
  const maxPages = 100;
  while (page <= maxPages) {
    const res = await getSyllabusTopics(SYLLABUS_TOPIC_DEFAULT_LIMIT, page, search, { courseId: cid, includeTotal: false });
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    if (chunk.length < SYLLABUS_TOPIC_DEFAULT_LIMIT) break;
    page += 1;
  }
  return all;
};

// 🔹 Create a new syllabus (supports file upload)
export const createSyllabusTopics = async (syllabusData) => {
  try {
    const response = await axiosInstance.post(API_URL, syllabusData);
    invalidateCourseList();
    return response.data;
  } catch (error) {
    console.error("Error creating syllabus topics", {
      error: error.response?.data,
      requestData: syllabusData
    });
    throw error;
  }
};

// 🔹 Update a syllabus
export const updateSyllabusTopics = async (syllabusId, syllabusData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${syllabusId}`, syllabusData);
    invalidateCourseList();
    return response.data;
  } catch (error) {
    console.error("Error updating syllabus", error);
    throw error;
  }
};

// 🔹 Delete a syllabus
export const deleteSyllabusTopics = async (syllabusId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${syllabusId}`);
    invalidateCourseList();
    return response.data;
  } catch (error) {
    console.error("Error deleting syllabus", error);
    throw error;
  }
};

// 🔹 Get syllabus by ID
export const getSyllabusTopicsById = async (syllabusId) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${syllabusId}`);
    return {
      data: response.data.data || response.data,
      status: response.status,
      message: response.data.message
    };
  } catch (error) {
    console.error("Error fetching syllabus details", error);
    throw error;
  }
};